import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Button,
  Col,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import Notiflix from "notiflix";
import { toast } from "react-toastify";

/* ===============================
   BASE64 → FILE CONVERTER
================================ */
const base64ToFile = (base64, filename) => {
  const arr = base64.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

/* ===============================
   COMPONENT
================================ */
const OpenCameraModal = ({
  showModal,
  setShowModal,
  setProfileImage,
  setLocation,
}) => {
  const [geoloc, setGeoloc] = useState("");
  const [selfieBase64, setSelfieBase64] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  /* ===============================
     CAMERA HANDLERS
  ================================ */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    } catch {
      toast.error("Camera access denied");
    }
  };

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  /* ===============================
     CAPTURE SELFIE
  ================================ */
  const takeSelfie = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;

    canvas.width = 400;
    canvas.height = 300;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const base64Image = canvas.toDataURL("image/jpeg", 0.8);
    setSelfieBase64(base64Image);

    getLocation();
  };

  /* ===============================
     GEO LOCATION
  ================================ */
  const getLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setGeoloc(`${latitude},${longitude}`);
      },
      () => toast.error("Location permission required")
    );
  };

  const isInIndia = (lat, lng) =>
    lat >= 6.5 && lat <= 37.6 && lng >= 68.7 && lng <= 97.25;

  /* ===============================
     START AUDIT
  ================================ */
  const handleStart = async () => {
    if (!selfieBase64) return toast.error("Please take selfie");
    if (!geoloc) return toast.error("Location not detected");

    const [lat, lng] = geoloc.split(",").map(Number);
    if (!isInIndia(lat, lng))
      return toast.warning("Audit allowed only inside India");

    try {
      Notiflix.Loading.standard();

      // Convert Base64 → File
      const selfieFile = base64ToFile(
        selfieBase64,
        `audit_selfie_${Date.now()}.jpg`
      );

      // // Prepare upload
      // const formData = new FormData();
      // formData.append("file", selfieFile);
      // formData.append("latitude", lat);
      // formData.append("longitude", lng);

      // // Upload API
      // await fetch("/api/upload-selfie", {
      //   method: "POST",
      //   body: formData,
      // });

      setProfileImage(selfieFile);
      setLocation({ lat, lng });
      setShowModal(false);
    } catch {
      toast.error("Upload failed");
    } finally {
      Notiflix.Loading.remove();
    }
  };

  /* ===============================
     EFFECTS
  ================================ */
  useEffect(() => {
    if (showModal) {
      setSelfieBase64(null);
      startCamera();
    } else {
      stopCamera();
    }
    return stopCamera;
  }, [showModal]);

  /* ===============================
     UI
  ================================ */
  return (
    <Modal isOpen={showModal} toggle={() => setShowModal(false)} centered>
      <ModalHeader toggle={() => setShowModal(false)}>
        Upload Photo
      </ModalHeader>

      <ModalBody>
        <Col sm="12">
          <video
            ref={videoRef}
            className={`w-100 ${selfieBase64 ? "d-none" : ""}`}
            playsInline
          />

          {selfieBase64 && (
            <img
              src={selfieBase64}
              alt="Captured preview"
              className="w-100"
              style={{ objectFit: "cover", height: "auto" }}
            />
          )}

          <canvas ref={canvasRef} className="d-none" />

          {geoloc && (
            <small className="d-block text-center mt-2">
              Location: <b>{geoloc}</b>
            </small>
          )}

          {!selfieBase64 ? (
            <Button color="primary" className="w-100 mt-2" onClick={takeSelfie}>
              Take Selfie
            </Button>
          ) : (
            <Button
              color="warning"
              className="w-100 mt-2"
              onClick={() => setSelfieBase64(null)}
            >
              Retake
            </Button>
          )}
        </Col>
      </ModalBody>

      <ModalFooter>
        <Button outline onClick={() => setShowModal(false)}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleStart}>
          Start
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default OpenCameraModal;
